import { useState, useEffect } from "react";
import { useStaticTranslation } from "@/lib/staticTranslations";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { alertToast } from "@/contexts/AlertToastContext";
import { Loader2, Search, Plus, Pencil, Trash2, Package, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { BarcodeScannerAnimation } from "@/components/BarcodeScannerAnimation";
import { BulkDeliveryScanner } from "@/components/BulkDeliveryScanner";

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email?: string;
}

interface RepairOrder {
  id: string;
  order_number: number;
  user_id: string | null;
  guest_phone: string | null;
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  user?: UserProfile;
}

const STATUS_OPTIONS = [
  { value: "pending", label: "ממתין", color: "bg-yellow-100 text-yellow-800" },
  { value: "in_repair", label: "בתיקון", color: "bg-blue-100 text-blue-800" },
  { value: "completed", label: "הושלם", color: "bg-green-100 text-green-800" },
  { value: "waiting_in_store", label: "מחכה בחנות", color: "bg-orange-100 text-orange-800" },
  { value: "shipped", label: "נשלח", color: "bg-purple-100 text-purple-800" },
  { value: "delivered", label: "נמסר", color: "bg-emerald-100 text-emerald-800" },
  { value: "cancelled", label: "בוטל", color: "bg-red-100 text-red-800" },
];

export const AdminRepairManagement = () => {
  const { t: at } = useStaticTranslation("admin");
  const [repairs, setRepairs] = useState<RepairOrder[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRepair, setEditingRepair] = useState<RepairOrder | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [smsBalance, setSmsBalance] = useState<number | null>(null);

  // Form state
  const [selectedUserId, setSelectedUserId] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [isGuestMode, setIsGuestMode] = useState(true); // Default to guest mode
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("in_repair");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [manualOrderNumber, setManualOrderNumber] = useState("");
  const [scanningMode, setScanningMode] = useState(true);
  const [orderNumberExists, setOrderNumberExists] = useState(false);
  const [checkingOrderNumber, setCheckingOrderNumber] = useState(false);


  useEffect(() => {
    loadData();
    loadSmsBalance();
  }, []);

  const loadSmsBalance = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("get-sms-balance");
      if (!error && data?.balance !== undefined) {
        setSmsBalance(typeof data.balance === "number" ? data.balance : parseInt(data.balance) || 0);
      }
    } catch (err) {
      console.error("Error loading SMS balance:", err);
    }
  };

  const loadData = async () => {
    try {
      // Load all repairs
      const { data: repairsData, error: repairsError } = await supabase
        .from("repair_orders")
        .select("*")
        .order("created_at", { ascending: false });

      if (repairsError) throw repairsError;

      // Load users with emails from edge function
      const { data: usersResponse, error: usersError } = await supabase.functions.invoke("get-users-with-emails");

      if (usersError) {
        console.error("Error loading users with emails:", usersError);
        // Fallback to profiles without emails
        const { data: profilesData } = await supabase.from("profiles").select("id, first_name, last_name, phone");
        setUsers(profilesData || []);

        const repairsWithUsers =
          repairsData?.map((repair) => ({
            ...repair,
            user: profilesData?.find((u) => u.id === repair.user_id),
          })) || [];
        setRepairs(repairsWithUsers);
      } else {
        const usersData = usersResponse?.users || [];
        setUsers(usersData);

        // Map repairs with user data
        const repairsWithUsers =
          repairsData?.map((repair) => ({
            ...repair,
            user: usersData.find((u: UserProfile) => u.id === repair.user_id),
          })) || [];

        setRepairs(repairsWithUsers);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      alertToast.error(at("dataLoadError", "שגיאה בטעינת הנתונים"));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const query = userSearchQuery.toLowerCase();
    return (
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query) ||
      user.phone?.includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  });

  const filteredRepairs = repairs.filter((repair) => {
    const query = searchQuery.toLowerCase();
    return (
      repair.order_number.toString().includes(query) ||
      repair.title.toLowerCase().includes(query) ||
      repair.guest_phone?.includes(query) ||
      repair.user?.first_name?.toLowerCase().includes(query) ||
      repair.user?.last_name?.toLowerCase().includes(query) ||
      repair.user?.phone?.includes(query) ||
      repair.user?.email?.toLowerCase().includes(query)
    );
  });

  const resetForm = () => {
    setSelectedUserId("");
    setGuestPhone("");
    setIsGuestMode(true); // Default to guest mode
    setTitle("");
    setDescription("");
    setStatus("in_repair");
    setUserSearchQuery("");
    setManualOrderNumber("");
    setScanningMode(true);
    setOrderNumberExists(false);
    setCheckingOrderNumber(false);
  };

  const checkOrderNumberExists = async (orderNumber: string): Promise<boolean> => {
    if (!orderNumber) return false;

    setCheckingOrderNumber(true);
    try {
      const { data, error } = await supabase
        .from("repair_orders")
        .select("id")
        .eq("order_number", parseInt(orderNumber))
        .maybeSingle();

      if (error) {
        console.error("Error checking order number:", error);
        return false;
      }

      return data !== null;
    } catch (err) {
      console.error("Error checking order number:", err);
      return false;
    } finally {
      setCheckingOrderNumber(false);
    }
  };

  const handleScanComplete = async (orderNumber: string) => {
    setManualOrderNumber(orderNumber);
    setScanningMode(false);

    // Check if order number already exists
    const exists = await checkOrderNumberExists(orderNumber);
    setOrderNumberExists(exists);
    if (exists) {
      alertToast.error(`${at("orderNumber", "מספר הזמנה")} ${orderNumber} ${at("orderExistsError", "כבר קיים במערכת")}`);
    }
  };

  const sendSmsNotification = async (
    orderId: string,
    orderNumber: number,
    newStatus: string,
    orderTitle: string,
    orderDescription: string | null,
    userPhone: string | null,
  ) => {
    if (!userPhone) {
      console.log("No phone number available for SMS");
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("send-repair-sms", {
        body: {
          orderId,
          orderNumber,
          newStatus,
          title: orderTitle,
          description: orderDescription,
          phone: userPhone,
        },
      });

      if (error) {
        console.error("Error sending SMS:", error);
        alertToast.error(at("smsError", "ההזמנה עודכנה אך שליחת ה-SMS נכשלה"));
      } else if (data?.success) {
        alertToast.success(at("smsSent", "SMS נשלח ללקוח"));
        loadSmsBalance();
      } else if (data?.error) {
        console.error("SMS error:", data.error);
        alertToast.error(`${at("smsErrorMsg", "שגיאה בשליחת SMS")}: ${data.error}`);
      }
    } catch (err) {
      console.error("Failed to send SMS notification:", err);
    }
  };

  const handleAddRepair = async () => {
    if (!selectedUserId || !title) {
      alertToast.error(at("fillRequired", "יש למלא את כל השדות הנדרשים"));
      return;
    }

    setIsSaving(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const { data: insertedData, error } = await supabase
        .from("repair_orders")
        .insert({
          user_id: selectedUserId,
          title,
          description: description || null,
          status: status as "pending" | "in_repair" | "completed" | "shipped" | "delivered" | "cancelled",
          created_by: session?.user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Find user phone for SMS
      const user = users.find((u) => u.id === selectedUserId);
      if (user?.phone && insertedData) {
        await sendSmsNotification(
          insertedData.id,
          insertedData.order_number,
          status,
          title,
          description || null,
          user.phone,
        );
      }

      alertToast.success(at("orderAdded", "הזמנה נוספה בהצלחה"));
      setIsAddDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error adding repair:", error);
      alertToast.error(at("orderAddError", "שגיאה בהוספת ההזמנה"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRepair = async () => {
    if (!editingRepair) return;

    const statusChanged = editingRepair.status !== status;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("repair_orders")
        .update({
          title,
          description: description || null,
          status: status as "pending" | "in_repair" | "completed" | "shipped" | "delivered" | "cancelled",
        })
        .eq("id", editingRepair.id);

      if (error) throw error;

      // Send SMS if status changed
      if (statusChanged && editingRepair.user?.phone) {
        await sendSmsNotification(
          editingRepair.id,
          editingRepair.order_number,
          status,
          title,
          description || null,
          editingRepair.user.phone,
        );
      }

      alertToast.success(at("orderUpdated", "ההזמנה עודכנה בהצלחה"));
      setIsEditDialogOpen(false);
      setEditingRepair(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error updating repair:", error);
      alertToast.error(at("orderUpdateError", "שגיאה בעדכון ההזמנה"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRepair = async (repairId: string) => {
    if (!confirm(at("deleteOrder", "האם למחוק את ההזמנה?"))) return;

    try {
      const { error } = await supabase.from("repair_orders").delete().eq("id", repairId);

      if (error) throw error;

      alertToast.success(at("orderDeleted", "ההזמנה נמחקה בהצלחה"));
      loadData();
    } catch (error) {
      console.error("Error deleting repair:", error);
      alertToast.error(at("orderDeleteError", "שגיאה במחיקת ההזמנה"));
    }
  };

  const openEditDialog = (repair: RepairOrder) => {
    setEditingRepair(repair);
    setTitle(repair.title);
    setDescription(repair.description || "");
    setStatus(repair.status);
    setIsEditDialogOpen(true);
  };

  const getStatusBadge = (statusValue: string) => {
    const statusOption = STATUS_OPTIONS.find((s) => s.value === statusValue);
    return (
      <Badge className={statusOption?.color || "bg-gray-100 text-gray-800"}>{statusOption?.label || statusValue}</Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleMarkAsDelivered = async (repair: RepairOrder) => {
    // If status is not delivered, update to delivered and send SMS
    if (repair.status !== "delivered") {
      try {
        const { error } = await supabase.from("repair_orders").update({ status: "delivered" }).eq("id", repair.id);

        if (error) throw error;

        // Send SMS notification
        const phoneToSend = repair.guest_phone || repair.user?.phone;
        if (phoneToSend) {
          await sendSmsNotification(
            repair.id,
            repair.order_number,
            "delivered",
            repair.title,
            repair.description || null,
            phoneToSend,
          );
        }

        alertToast.success(at("statusDelivered", "סטטוס שונה לנמסר"));
        loadData();
      } catch (error) {
        console.error("Error updating status:", error);
        alertToast.error(at("orderUpdateError", "שגיאה בעדכון הסטטוס"));
      }
    } else {
      alertToast.info(at("alreadyDelivered", "ההזמנה כבר סומנה כנמסרה"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* SMS Balance Display */}
      {smsBalance !== null && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-3 px-4 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">{at("smsBalance", "יתרת SMS")}:</span>
            <Badge variant="secondary" className="text-sm">
              {smsBalance} {at("messages", "הודעות")}
            </Badge>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative w-full sm:w-auto">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={at("searchPlaceholder", "חיפוש לפי מספר פנייה, שם, טלפון...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10 w-full sm:w-[300px]"
          />
        </div>

        <div className="flex gap-2">
          <BulkDeliveryScanner onDeliveryComplete={loadData} />

          <Dialog
            open={isAddDialogOpen}
            onOpenChange={(open) => {
              setIsAddDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 ml-2" />
                {at("addOrder", "הוספת הזמנה/תיקון")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
              <DialogHeader>
                <DialogTitle className="text-right">{at("newOrder", "הזמנה/תיקון חדש")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Barcode scanning animation */}
                {scanningMode ? (
                  <BarcodeScannerAnimation
                    isActive={isAddDialogOpen && scanningMode}
                    onScanComplete={handleScanComplete}
                  />
                ) : (
                  <>
                    {/* Order number display */}
                    <div
                      className={`flex items-center justify-between p-3 rounded-md ${orderNumberExists ? "bg-destructive/10 border border-destructive" : "bg-muted"}`}
                    >
                      <span className="text-sm text-muted-foreground">{at("orderNumber", "מספר הזמנה")}:</span>
                      <div className="flex items-center gap-2">
                        {checkingOrderNumber && <Loader2 className="h-4 w-4 animate-spin" />}
                        <span className={`font-mono text-lg font-bold ${orderNumberExists ? "text-destructive" : ""}`}>
                          #{manualOrderNumber}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setManualOrderNumber("");
                          setScanningMode(true);
                          setOrderNumberExists(false);
                        }}
                      >
                        {at("rescan", "סריקה מחדש")}
                      </Button>
                    </div>

                    {orderNumberExists && (
                      <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/5 p-2 rounded">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <span>{at("orderExists", "מספר הזמנה זה כבר קיים במערכת. יש לסרוק מספר אחר.")}</span>
                      </div>
                    )}

                    {/* Toggle between registered user and guest */}
                    <div className="flex gap-2 mb-4">
                      <Button
                        type="button"
                        variant={isGuestMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setIsGuestMode(true);
                          setSelectedUserId("");
                          setUserSearchQuery("");
                        }}
                        className="flex-1"
                      >
                        {at("guestCustomer", "לקוח אורח")}
                      </Button>
                      <Button
                        type="button"
                        variant={!isGuestMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setIsGuestMode(false);
                          setGuestPhone("");
                        }}
                        className="flex-1"
                      >
                        {at("registeredCustomer", "לקוח רשום")}
                      </Button>
                    </div>

                    {isGuestMode ? (
                      <div className="space-y-2">
                        <Label>{at("guestPhone", "מספר טלפון של האורח *")}</Label>
                        <Input
                          type="tel"
                          placeholder="05X-XXXXXXX"
                          value={guestPhone}
                          onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, ""))}
                          className="text-left"
                          dir="ltr"
                        />
                        <p className="text-xs text-muted-foreground">
                          {at("guestPhoneNote", "הלקוח יוכל לעקוב אחרי ההזמנה דרך מספר הטלפון באמצעות קוד OTP")}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label>{at("searchCustomer", "חיפוש לקוח")}</Label>
                          <Input
                            placeholder={at("searchCustomerPlaceholder", "חיפוש לפי שם, טלפון או אימייל...")}
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            className="text-right"
                          />
                        </div>

                        {userSearchQuery && (
                          <div className="max-h-[200px] overflow-y-auto border rounded-md">
                            {filteredUsers.length === 0 ? (
                              <p className="p-4 text-center text-muted-foreground">{at("noCustomersFound", "לא נמצאו לקוחות")}</p>
                            ) : (
                              filteredUsers.map((user) => (
                                <button
                                  key={user.id}
                                  onClick={() => {
                                    setSelectedUserId(user.id);
                                    setUserSearchQuery(
                                      `${user.first_name || ""} ${user.last_name || ""} - ${user.phone || user.email || ""}`,
                                    );
                                  }}
                                  className={`w-full p-3 text-right hover:bg-muted transition-colors border-b last:border-b-0 ${selectedUserId === user.id ? "bg-primary/10" : ""}`}
                                >
                                  <div className="font-medium">
                                    {user.first_name} {user.last_name}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {user.phone && <span>{user.phone}</span>}
                                    {user.phone && user.email && <span> • </span>}
                                    {user.email && <span>{user.email}</span>}
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  {at("cancel", "ביטול")}
                </Button>
                <Button
                  onClick={async () => {
                    const hasValidUser = !isGuestMode && selectedUserId;
                    const hasValidGuest = isGuestMode && guestPhone.length >= 9;

                    if ((!hasValidUser && !hasValidGuest) || !manualOrderNumber) {
                      alertToast.error(at("fillRequired", "יש למלא את כל השדות הנדרשים"));
                      return;
                    }

                    setIsSaving(true);
                    try {
                      const {
                        data: { session },
                      } = await supabase.auth.getSession();

                      const insertData: any = {
                        order_number: parseInt(manualOrderNumber),
                        title: "הזמנה/תיקון",
                        description: null,
                        status: "in_repair" as "pending" | "in_repair" | "completed" | "shipped" | "delivered" | "cancelled",
                        created_by: session?.user?.id,
                      };

                      if (isGuestMode) {
                        insertData.guest_phone = guestPhone;
                        insertData.user_id = null;
                      } else {
                        insertData.user_id = selectedUserId;
                        insertData.guest_phone = null;
                      }


                      const { data: insertedData, error } = await supabase
                        .from("repair_orders")
                        .insert(insertData)
                        .select()
                        .single();

                      if (error) {
                        if (error.code === "23505") {
                          alertToast.error(at("orderExistsError", "מספר הזמנה זה כבר קיים במערכת"));
                        } else {
                          throw error;
                        }
                        return;
                      }

                      // Find user for SMS and print (registered user)
                      const user = !isGuestMode ? users.find((u) => u.id === selectedUserId) : undefined;

                      // Send SMS - for registered users use their phone, for guests use guestPhone
                      const phoneToSend = isGuestMode ? guestPhone : user?.phone;
                      if (phoneToSend && insertedData) {
                        await sendSmsNotification(
                          insertedData.id,
                          insertedData.order_number,
                          "in_repair",
                          "הזמנה/תיקון",
                          null,
                          phoneToSend,
                        );
                      }

                      alertToast.success(at("orderAdded", "הזמנה נוספה בהצלחה"));
                      setIsAddDialogOpen(false);
                      resetForm();
                      loadData();
                    } catch (error) {
                      console.error("Error adding repair:", error);
                      alertToast.error(at("orderAddError", "שגיאה בהוספת ההזמנה"));
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  disabled={
                    isSaving ||
                    orderNumberExists ||
                    checkingOrderNumber ||
                    (!isGuestMode && !selectedUserId) ||
                    (isGuestMode && guestPhone.length < 9) ||
                    !manualOrderNumber
                  }
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : <Plus className="h-4 w-4 ml-2" />}
                  {at("addRepair", "הוסף הזמנה")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingRepair(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">{at("editOrder", "עריכת הזמנה/תיקון")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{at("titleField", "כותרת *")}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="text-right" />
            </div>

            <div className="space-y-2">
              <Label>{at("description", "תיאור")}</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="text-right" />
            </div>

            <div className="space-y-2">
              <Label>{at("status", "סטטוס")}</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="text-right">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {at("cancel", "ביטול")}
            </Button>
            <Button onClick={handleUpdateRepair} disabled={isSaving || !title}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : null}
              {at("save", "שמור")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repairs List */}
      {filteredRepairs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchQuery ? at("noOrdersFound", "לא נמצאו הזמנות התואמות לחיפוש") : at("noOrders", "אין הזמנות/תיקונים כרגע")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredRepairs.map((repair) => (
            <Card key={repair.id}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        #{repair.order_number}
                      </Badge>
                      <h3 className="font-medium">{repair.title}</h3>
                      {getStatusBadge(repair.status)}
                    </div>
                    {repair.description && <p className="text-sm text-muted-foreground mb-2">{repair.description}</p>}
                    <div className="text-sm text-muted-foreground">
                      {repair.guest_phone ? (
                        <>
                          <p>{at("guest", "לקוח אורח")}</p>
                          <p>{at("phone", "טלפון")}: {repair.guest_phone}</p>
                        </>
                      ) : (
                        <>
                          <p>
                            {at("customer", "לקוח")}: {repair.user?.first_name} {repair.user?.last_name}
                          </p>
                          {repair.user?.phone && <p>{at("phone", "טלפון")}: {repair.user.phone}</p>}
                          {repair.user?.email && <p>{at("email", "אימייל")}: {repair.user.email}</p>}
                        </>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {at("created", "נוצר")}: {formatDate(repair.created_at)}
                      {repair.updated_at !== repair.created_at && ` • ${at("updated", "עודכן")}: ${formatDate(repair.updated_at)}`}
                    </p>
                  </div>
                  <div className="flex gap-2 self-start">
                    {repair.status !== "delivered" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMarkAsDelivered(repair)}
                        title={at("markDelivered", "סמן כנמסר")}
                      >
                        <Package className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(repair)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteRepair(repair.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
